import {
  ArquivoRespostaMeta,
  CampoRespostaItem,
  FormularioRespostaI,
} from '../../../../interfaces/form-flow';

export type ValorRespostaFormatado =
  | { tipo: 'texto'; texto: string }
  | { tipo: 'arquivo'; arquivos: ArquivoRespostaMeta[] }
  | { tipo: 'vazio' };

const isArquivoMeta = (value: unknown): value is ArquivoRespostaMeta => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const arquivo = value as Record<string, unknown>;
  return (
    arquivo.id !== undefined &&
    arquivo.id !== null &&
    typeof arquivo.nome === 'string' &&
    typeof arquivo.url === 'string'
  );
};

/**
 * Converte o valor bruto de um campo de resposta em formato exibível na UI.
 */
export const formatarValorResposta = (value: unknown): ValorRespostaFormatado => {
  if (value === null || value === undefined || value === '') {
    return { tipo: 'vazio' };
  }
  if (isArquivoMeta(value)) {
    return { tipo: 'arquivo', arquivos: [value] };
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return { tipo: 'vazio' };
    }
    if (value.every(isArquivoMeta)) {
      return { tipo: 'arquivo', arquivos: value };
    }
    const textos = value.map(item => {
      if (item === null || item === undefined) {
        return '';
      }
      if (typeof item === 'object') {
        const obj = item as Record<string, unknown>;
        if (obj.label !== undefined) {
          return String(obj.label);
        }
        if (obj.titulo !== undefined) {
          return String(obj.titulo);
        }
        return JSON.stringify(item);
      }
      return String(item);
    });
    return { tipo: 'texto', texto: textos.filter(Boolean).join(', ') };
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (obj.label !== undefined) {
      return { tipo: 'texto', texto: String(obj.label) };
    }
    if (obj.titulo !== undefined) {
      return { tipo: 'texto', texto: String(obj.titulo) };
    }
    if (typeof obj.resposta !== 'undefined') {
      return formatarValorResposta(obj.resposta);
    }
    return { tipo: 'texto', texto: JSON.stringify(value) };
  }
  if (typeof value === 'boolean') {
    return { tipo: 'texto', texto: value ? 'Sim' : 'Não' };
  }
  return { tipo: 'texto', texto: String(value) };
};

/**
 * Extrai lista ordenada de campos a partir do mapa `resposta` da API.
 */
export const mapearCamposResposta = (
  resposta: Record<string, CampoRespostaItem> | null | undefined,
): Array<{ chave: string; titulo: string; formatado: ValorRespostaFormatado }> => {
  if (!resposta || typeof resposta !== 'object') {
    return [];
  }
  return Object.keys(resposta).map(chave => {
    const item = resposta[chave];
    const titulo =
      (item && typeof item === 'object' && item.titulo) || `Campo ${chave}`;
    const valorBruto =
      item && typeof item === 'object' && 'resposta' in item
        ? item.resposta
        : item;
    return {
      chave,
      titulo: String(titulo),
      formatado: formatarValorResposta(valorBruto),
    };
  });
};

/**
 * Coleta apenas os arquivos enviados nas respostas de formulário.
 */
export const extrairArquivosDasRespostas = (
  answers: FormularioRespostaI[],
): ArquivoRespostaMeta[] => {
  if (!answers || answers.length === 0) {
    return [];
  }
  const arquivos: ArquivoRespostaMeta[] = [];
  const idsVistos = new Set<string>();
  answers.forEach(formAnswer => {
    mapearCamposResposta(formAnswer.resposta).forEach(campo => {
      if (campo.formatado.tipo !== 'arquivo') {
        return;
      }
      campo.formatado.arquivos.forEach(arquivo => {
        const chaveUnica = String(arquivo.id);
        if (idsVistos.has(chaveUnica)) {
          return;
        }
        idsVistos.add(chaveUnica);
        arquivos.push(arquivo);
      });
    });
  });
  return arquivos;
};

/**
 * Resolve o rótulo do formulário a partir do campo `formulario` da API.
 */
export const obterTituloFormulario = (
  formulario: string | { slug?: string; titulo?: string },
): string => {
  if (typeof formulario === 'string') {
    return formulario;
  }
  if (formulario && typeof formulario === 'object') {
    return formulario.titulo || formulario.slug || 'Formulário';
  }
  return 'Formulário';
};
